import React, { useEffect, useRef, useState, useMemo } from 'react';
import './CategoryTreeSelect.css';

/**
 * options: flat array or tree (nodes with `children`)
 * onlyLeafCategories: searchable flat list of leaf nodes with full path — easier than expanding a tree.
 */
const CategoryTreeSelect = ({
  options = [],
  value = '',
  onChange,
  placeholder = 'Select category',
  disabled = false,
  onlyLeafCategories = false,
  className = '',
  searchPlaceholder,
  autoFocusSearch = true,
}) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [expanded, setExpanded] = useState({}); // map id -> boolean
  const ref = useRef(null);
  const searchInputRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (ref.current && !ref.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // detect if options is a tree (has children) or flat
  const isTree = Array.isArray(options) && options.some(o => o && Array.isArray(o.children));

  /** Leaf nodes only (for product subcategories) — one row per selectable category with full path. */
  const leafRows = useMemo(() => {
    const out = [];
    const walk = (nodes = [], ancestors = []) => {
      if (!nodes) return;
      nodes.forEach((n) => {
        if (!n || n._id == null) return;
        const kids = Array.isArray(n.children) ? n.children : [];
        if (kids.length > 0) {
          walk(kids, [...ancestors, n]);
        } else {
          const path = [...ancestors.map((a) => a.name), n.name].filter(Boolean);
          out.push({
            _id: n._id,
            name: n.name,
            path,
            pathLabel: path.join(' › '),
          });
        }
      });
    };
    if (isTree && Array.isArray(options)) {
      walk(options, []);
      return out;
    }
    if (!isTree && Array.isArray(options)) {
      return options.map((o) => ({
        _id: o._id,
        name: o.name || o.label || String(o._id),
        path: [o.name || o.label || String(o._id)],
        pathLabel: o.name || o.label || String(o._id),
      }));
    }
    return out;
  }, [options, isTree]);

  // flatten tree for search results
  const flattened = useMemo(() => {
    const out = [];
    const walk = (nodes = [], depth = 0, ancestors = []) => {
      if (!nodes) return;
      nodes.forEach(n => {
        out.push({ _id: n._id, name: n.name, depth, path: [...ancestors.map(a => a.name), n.name] });
        if (n.children) walk(n.children, depth + 1, [...ancestors, n]);
      });
    };
    if (isTree) walk(options, 0, []);
    else if (Array.isArray(options)) out.push(...options.map((o, i) => ({ _id: o._id, name: o.name || o.label || String(o._id), depth: o.depth || 0, path: [o.name || o.label || String(o._id)] })));
    return out;
  }, [options, isTree]);

  const selectedFlat = flattened.find((o) => o._id === value) || null;
  const selectedLeaf = leafRows.find((o) => o._id === value) || null;
  const selected = onlyLeafCategories ? selectedLeaf || selectedFlat : selectedFlat;

  const defaultSearchPh = onlyLeafCategories
    ? 'Type to search name or path…'
    : 'Search categories…';

  const filterByQuery = (rows, q) => {
    if (!q || !q.trim()) return rows;
    const lower = q.trim().toLowerCase();
    return rows.filter((o) => {
      const pathStr =
        o.pathLabel ||
        (Array.isArray(o.path) ? o.path.join(' › ') : '') ||
        o.name ||
        '';
      return (
        String(o.name).toLowerCase().includes(lower) ||
        pathStr.toLowerCase().includes(lower)
      );
    });
  };

  const qTrim = query.trim();
  const searchResults = qTrim
    ? filterByQuery(onlyLeafCategories ? leafRows : flattened, qTrim)
    : null;

  const leafListFiltered = useMemo(
    () => filterByQuery(leafRows, qTrim),
    [leafRows, qTrim]
  );

  const toggle = (id) => setExpanded(prev => ({ ...prev, [id]: !prev[id] }));

  const handleSelect = (id) => {
    onChange && onChange(id);
    setOpen(false);
    setQuery('');
  };

  useEffect(() => {
    if (!open || !autoFocusSearch) return;
    const t = requestAnimationFrame(() => {
      searchInputRef.current?.focus();
    });
    return () => cancelAnimationFrame(t);
  }, [open, autoFocusSearch]);

  // recursive renderer for tree view
  const renderNode = (node, depth = 0) => {
    const hasChildren = Array.isArray(node.children) && node.children.length > 0;
    const isOpen = !!expanded[node._id];
    return (
      <div key={node._id}>
        <div
          className={`cts-item cts-item--tree ${value === node._id ? 'selected' : ''}`}
          style={{ paddingLeft: `${depth * 16 + 8}px` }}
        >
          {hasChildren ? (
            <button
              type="button"
              className={`cts-caret-btn ${isOpen ? 'open' : ''}`}
              onClick={(e) => {
                e.stopPropagation();
                toggle(node._id);
              }}
              aria-label={isOpen ? 'Collapse' : 'Expand'}
            >
              ▸
            </button>
          ) : (
            <span className="cts-spacer" />
          )}
          <div
            className="cts-name"
            role="option"
            aria-selected={value === node._id}
            onClick={() => handleSelect(node._id)}
            onKeyDown={(e) => e.key === 'Enter' && handleSelect(node._id)}
          >
            {node.name}
          </div>
        </div>
        {hasChildren && isOpen && (
          <div className="cts-children">
            {node.children.map((c) => renderNode(c, depth + 1))}
          </div>
        )}
      </div>
    );
  };

  const renderLeafPicker = () => (
    <>
      <div className="cts-leaf-hint">
        Pick a <strong>leaf</strong> category (end of branch). Search is fastest — no need to expand folders.
      </div>
      <div className="cts-list cts-list--leaf" role="listbox">
        {leafListFiltered.length === 0 ? (
          <div className="cts-empty">
            {leafRows.length === 0
              ? 'No leaf categories available.'
              : 'No matches. Try another search.'}
          </div>
        ) : (
          leafListFiltered.map((r) => (
            <button
              key={r._id}
              type="button"
              className={`cts-item cts-item--leaf ${value === r._id ? 'selected' : ''}`}
              onClick={() => handleSelect(r._id)}
              role="option"
              aria-selected={value === r._id}
            >
              <span className="cts-leaf-name">{r.name}</span>
              <span className="cts-leaf-path">{r.pathLabel}</span>
            </button>
          ))
        )}
      </div>
    </>
  );

  const controlLabel = () => {
    if (!selected) return <span className="cts-value">{placeholder}</span>;
    const pathLine =
      selected.pathLabel ||
      (Array.isArray(selected.path) ? selected.path.join(' › ') : '');
    if (onlyLeafCategories && pathLine) {
      return (
        <span className="cts-value cts-value--stacked">
          <span className="cts-value-title">{selected.name}</span>
          <span className="cts-value-sub">{pathLine}</span>
        </span>
      );
    }
    return <span className="cts-value">{selected.name || placeholder}</span>;
  };

  return (
    <div
      className={`category-tree-select ${disabled ? 'disabled' : ''} ${onlyLeafCategories ? 'category-tree-select--leaf-mode' : ''} ${className}`.trim()}
      ref={ref}
    >
      <button
        type="button"
        className="cts-control"
        onClick={() => !disabled && setOpen((s) => !s)}
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        {controlLabel()}
        <span className={`cts-caret ${open ? 'open' : ''}`}>▾</span>
      </button>

      {open && (
        <div
          className={`cts-panel ${onlyLeafCategories ? 'cts-panel--leaf' : ''}`}
        >
          <div className="cts-search">
            <input
              ref={searchInputRef}
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={searchPlaceholder || defaultSearchPh}
              aria-label="Search categories"
            />
          </div>
          {onlyLeafCategories ? (
            renderLeafPicker()
          ) : (
            <div className="cts-list" role="listbox">
              {qTrim ? (
                searchResults.length === 0 ? (
                  <div className="cts-empty">No categories</div>
                ) : (
                  searchResults.map((r) => (
                    <div
                      key={r._id}
                      className={`cts-item cts-item--search-hit ${value === r._id ? 'selected' : ''}`}
                      onClick={() => handleSelect(r._id)}
                      role="option"
                      aria-selected={value === r._id}
                    >
                      <div style={{ paddingLeft: `${(r.depth || 0) * 16 + 8}px` }}>
                        <div className="cts-name">{r.name}</div>
                        <div className="cts-path">{r.path.join(' › ')}</div>
                      </div>
                    </div>
                  ))
                )
              ) : isTree && options.length === 0 ? (
                <div className="cts-empty">No categories</div>
              ) : isTree ? (
                options.map((node) => renderNode(node, 0))
              ) : (
                flattened.map((node) => (
                  <div
                    key={node._id}
                    className={`cts-item cts-item--tree ${value === node._id ? 'selected' : ''}`}
                    onClick={() => handleSelect(node._id)}
                    style={{ paddingLeft: `${(node.depth || 0) * 16 + 8}px` }}
                  >
                    <div className="cts-name">{node.name}</div>
                  </div>
                ))
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default CategoryTreeSelect;
